/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { graphql } from 'react-apollo';

import gql from 'graphql-tag';
import { compose } from 'recompose';

import user from 'js/lib/user';

import { Accordion, AccordionHeader, AccordionPanel, Dialog, Grid, InlineNotification } from '@coursera/cds-core';
import type {
  CreateAdminUserFilterRequest,
  CreateAdminUserFilterResponse,
  DeleteAdminUserFilterRequest,
  DeleteAdminUserFilterResponse,
  SearchAdminUserFilterRequest,
  SearchAdminUserFilterResponse,
} from '@coursera/grpc-types-entsavedfilters/coursera/proto/entsavedfilters/v1beta1/enterprise_saved_filters_api';

import { useThirdPartyOrganization } from 'bundles/admin-dashboard/components/ThirdPartyOrganizationProvider';
import { TrackedCdsButton } from 'bundles/common/components/withSingleTracked';
import { UserFilterDescription } from 'bundles/enterprise-admin-user-filters/components/UserFilterDescription';
import { useUserFilter } from 'bundles/enterprise-admin-user-filters/context/userFilterContext';
import type { SavedAdminFilterFromServer } from 'bundles/enterprise-admin-user-filters/types';
import {
  getValidationError,
  mapUserFilterFromClientToServerModel,
  mapUserFilterFromServerToClientModel,
} from 'bundles/enterprise-admin-user-filters/utils';
import { Button, DebouncedTextField, Spacer } from 'bundles/enterprise-ui';

import _t from 'i18n!nls/enterprise-admin-user-filters';

const { HeadingGroup, Content, Actions } = Dialog;

type PropsFromCaller = {
  open: boolean;
  organizationId: string;
  onClose: () => void;
};

type PropsFromGraphql = {
  createAdminUserFilter: (request: {
    variables: {
      input: CreateAdminUserFilterRequest;
    };
  }) => Promise<{
    data: {
      CreateAdminUserFilterMutation: {
        adminUserFilter: CreateAdminUserFilterResponse;
      };
    };
  }>;
  deleteAdminUserFilter: (request: {
    variables: {
      input: DeleteAdminUserFilterRequest;
    };
  }) => Promise<{ data: DeleteAdminUserFilterResponse }>;
};

type PropsFromGraphql2 = {
  fetchDuplicateFilterId: (name: string) => Promise<string> | undefined;
};

type Props = PropsFromCaller & PropsFromGraphql & PropsFromGraphql2;

export const SaveViewDialogBase = ({
  open,
  fetchDuplicateFilterId,
  deleteAdminUserFilter,
  createAdminUserFilter,
  onClose,
}: Props) => {
  const { userFilter, setUserFilter } = useUserFilter();
  const [viewName, setViewName] = React.useState<string>('');
  const [duplicateFilterId, setDuplicateFilterId] = React.useState<string | undefined>();
  const thirdPartyOrganization = useThirdPartyOrganization();
  const [error, setError] = React.useState<string | undefined>();
  const close = () => {
    setError(undefined);
    setDuplicateFilterId(undefined);
    setViewName('');
    onClose();
  };
  const handleSaveView = async () => {
    try {
      const name = viewName.trim();
      const validationError = getValidationError(name);
      if (validationError) {
        throw validationError;
      }
      setError(undefined);
      const filter = mapUserFilterFromClientToServerModel(userFilter.filter);
      const input = {
        organizationId: thirdPartyOrganization.id,
        filterName: name,
        filterPurpose: 'FILTER_PURPOSE_REUSE',
        filter,
      };
      if (duplicateFilterId) {
        try {
          await deleteAdminUserFilter({ variables: { input: { filterId: duplicateFilterId } } });
        } catch {
          // Network error: Cannot read properties of undefined (reading 'selections')
          // this error is thrown by apollo, because the mutation does not return anything at all
        }
      }
      const response = await createAdminUserFilter({ variables: { input } });
      const savedFilter = response.data.CreateAdminUserFilterMutation.adminUserFilter as SavedAdminFilterFromServer;
      setUserFilter({
        ...savedFilter,
        filter: mapUserFilterFromServerToClientModel(savedFilter),
      });
      close();
    } catch (caughtError) {
      setError(typeof caughtError === 'string' ? caughtError : _t('Something went wrong!'));
    }
  };

  return (
    <Dialog aria-describedby="save-view-content" open={open} variant="standard" onClose={onClose}>
      <HeadingGroup
        supportText={_t(
          'Save your current selection of filters as a View, which can be recalled by you and fellow administrators.'
        )}
      >
        {_t('Save view')}
      </HeadingGroup>
      <Content id="save-view-content">
        <Accordion>
          <AccordionHeader label={_t('Filters to be saved in this view')} />
          <AccordionPanel>
            <Spacer size={8} />
            <UserFilterDescription userFilter={userFilter.filter} enrichedData={userFilter.enrichedData} />
          </AccordionPanel>
        </Accordion>
        <Spacer size={24} />
        <DebouncedTextField
          type="text"
          fullWidth
          label={_t('View name')}
          placeholder={_t('Please enter a name for your view')}
          defaultValue={viewName}
          onChange={async (newName) => {
            setViewName(newName);
            setError(undefined);
            setDuplicateFilterId(await fetchDuplicateFilterId(newName));
          }}
          data-testid="save-view-dialog-text-field"
          autoFocus
        />
        {error && (
          <React.Fragment>
            <Spacer size={24} />
            <InlineNotification severity="error">{error}</InlineNotification>
          </React.Fragment>
        )}
        {duplicateFilterId && (
          <React.Fragment>
            <Spacer size={24} />
            <InlineNotification severity="warning">
              {_t(
                'There is an existing saved view with the same name. If you choose to overwrite, this new view will replace that existing view.'
              )}
            </InlineNotification>
          </React.Fragment>
        )}
      </Content>
      <Actions>
        <Grid container spacing={24}>
          <Grid item>
            <TrackedCdsButton
              trackingName="save_view_dialog_save_view"
              variant="primary"
              size="medium"
              onClick={handleSaveView}
              data-testid="save_view_dialog_save_view"
              disabled={!viewName}
            >
              {duplicateFilterId ? _t('Overwrite') : _t('Save view')}
            </TrackedCdsButton>
          </Grid>
          <Grid item>
            <Button variant="secondary" size="medium" onClick={close}>
              {_t('Cancel')}
            </Button>
          </Grid>
        </Grid>
      </Actions>
    </Dialog>
  );
};

export const SaveViewDialogQuery = gql`
  query SaveViewDialogQuery($input: SearchAdminUserFilterRequest!) {
    SaveViewDialogQuery(input: $input)
      @rest(
        type: "SearchAdminUserFilter"
        path: "grpc/entsavedfilters/v1beta1/EnterpriseSavedFiltersAPI/SearchAdminUserFilter"
        method: "POST"
      ) {
      adminUserFilters
    }
  }
`;

export const createAdminUserFilterMutation = gql`
  mutation CreateAdminUserFilterMutation($input: CreateAdminUserFilterRequest!) {
    CreateAdminUserFilterMutation(input: $input)
      @rest(
        type: "CreateAdminUserFilter"
        path: "grpc/entsavedfilters/v1beta1/EnterpriseSavedFiltersAPI/CreateAdminUserFilter"
        method: "POST"
      ) {
      adminUserFilter
    }
  }
`;

export const DeleteAdminUserFilterMutation = gql`
  mutation DeleteAdminUserFilterMutation($input: DeleteAdminUserFilterRequest!) {
    DeleteAdminUserFilterMutation(input: $input)
      @rest(
        type: "DeleteAdminUserFilter"
        path: "grpc/entsavedfilters/v1beta1/EnterpriseSavedFiltersAPI/DeleteAdminUserFilter"
        method: "POST"
      ) {
      NoResponse
    }
  }
`;

export default compose<Props, PropsFromCaller>(
  graphql(DeleteAdminUserFilterMutation, {
    name: 'deleteAdminUserFilter',
  }),
  graphql(createAdminUserFilterMutation, {
    name: 'createAdminUserFilter',
  }),
  graphql<
    PropsFromCaller,
    { SaveViewDialogQuery: SearchAdminUserFilterResponse },
    { input: SearchAdminUserFilterRequest },
    PropsFromGraphql2
  >(SaveViewDialogQuery, {
    options: ({ organizationId }: PropsFromCaller) => ({
      variables: {
        input: {
          organizationId,
          filterType: 'FILTER_TYPE_USER_V1',
          filterName: '',
          createdBy: [user.get().id],
        } as SearchAdminUserFilterRequest,
      },
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    }),
    skip: (props) => !props.open,
    props: ({ data, ownProps: { organizationId } }) => {
      return {
        fetchDuplicateFilterId: (filterName: string) =>
          data
            ?.refetch({
              input: {
                organizationId,
                filterType: 'FILTER_TYPE_USER_V1',
                filterName,
                createdBy: [user.get().id],
              } as SearchAdminUserFilterRequest,
            })
            .then((response: { data: { SaveViewDialogQuery: SearchAdminUserFilterResponse } }) => {
              return response.data?.SaveViewDialogQuery?.adminUserFilters?.find(
                (_) => _.filterName.toLowerCase() === filterName.toLowerCase()
              )?.filterId;
            }),
      };
    },
  })
)(SaveViewDialogBase);
